<template>
  <span>
    <v-icon class="mr-2" color="primary" @click.stop="dialogClicked()"
      >mdi-share-variant</v-icon
    >

    <v-dialog v-model="dialog" width="50%">
      <v-card class="rounded-lg">
        <v-card-title color="white" primary-title>
          <v-icon color="primary">mdi-share-variant-outline</v-icon>
          <span class="primary--text text--darken-3 title pl-2"
            >Share {{ collection.label }}</span
          >
        </v-card-title>

        <v-card-text class="pt-4">
          <div
            v-if="collection.group_id != null"
            class="pa-1 yellow lighten-4 mb-5"
          >
            <v-container fluid fill-width>
              <v-row>
                <v-col cols="1">
                  <v-icon class="pa-1" color="warning" medium>warning</v-icon>
                </v-col>
                <v-col cols="11">
                  <span class="font-weight-bold">WARNING:</span> This Study
                  Dataset is currently shared with the group
                  <span class="font-weight-bold">{{
                    getGroupName(collection.group_id)
                  }}</span
                  >. Sharing it with a different group or unsharing it will
                  revoke that access.
                </v-col>
              </v-row>
            </v-container>
          </div>

          Share {{ collection.label }} with the following group:

          <v-data-table
            v-model="selectedGroup"
            :items="sharingGroups"
            item-key="group_id"
            show-select
            single-select
            dense
            :headers="getTableHeaders()"
            class="pt-4"
          >
            <template v-slot:item.name="{ item }">
              <td class="subtitle-1 text-xs-right pa-0 ma-0">
                {{ item.name }}
                <span v-if="collection.group_id == item.group_id"
                  ><v-icon class="pa-1" color="warning" medium
                    >warning</v-icon
                  ></span
                >
              </td>
            </template>

            <template v-slot:item.users="{ item }">
              <td class="pa-0 ma-0" style="text-align: left;">
                <v-tooltip top color="primary">
                  <template v-slot:activator="{ on: tooltip }">
                    <span v-on="{ ...tooltip }">
                      <v-chip label class="primary ma-1 ml-4">{{
                        item.users.length
                      }}</v-chip>
                    </span>
                  </template>
                  <div class="subtitle-1 my-2">
                    {{ item.name }} users:<br clear="both" />
                    <ul class="py-4">
                      <li
                        v-for="(u, ui) in item.users"
                        :key="'uli-' + ui"
                        class="ma-0 pa-0"
                      >
                        {{ u.name
                        }}<v-icon v-if="u.is_leader" color="white" class="pl-1"
                          >mdi-human-male-board</v-icon
                        ><v-icon
                          v-if="u.is_contributor"
                          color="white"
                          class="pl-1"
                          >mdi-share-variant</v-icon
                        ><br clear="both" />
                      </li>
                    </ul>
                    <span class="font-weight-bold pt-2 mb-4">Group Roles:</span>
                    <v-icon color="white" class="pl-2"
                      >mdi-human-male-board</v-icon
                    >
                    Leader
                    <v-icon color="white" class="pl-2"
                      >mdi-share-variant</v-icon
                    >
                    Contributor
                  </div>
                </v-tooltip>
              </td>
            </template>
          </v-data-table>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="dialog = false">
            <v-icon left>close</v-icon> Cancel
          </v-btn>
          <v-btn
            color="primary"
            :disabled="
              (selectedGroup.length == 0 && collection.group_id == null) ||
                (selectedGroup.length > 0 &&
                  selectedGroup[0].group_id == collection.group_id)
            "
            @click="shareOrUnshareCollection"
          >
            <v-icon left>mdi-share-variant</v-icon>
            {{
              selectedGroup.length == 0 && collection.group_id != null
                ? 'Unshare'
                : 'Share'
            }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import axios from 'axios';
import { mapActions, mapState } from 'vuex';
import { state as authState } from '@/store/modules/auth/types';
import { actions as notifyActions } from '@/store/modules/notifications/types';

export default {
  props: {
    collection: {
      type: Object,
      required: true,
    },
    gid2group: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    dialog: false,
    selectedGroup: [],
  }),
  computed: {
    ...mapState('auth', {
      user: authState.USER,
    }),
    sharingGroups() {
      let ulg = [];
      if (this.user != null) {
        this.user.groups.forEach(g => {
          if (g.is_leader || g.is_contributor) ulg.push(g);
        });
      }
      return ulg;
    },
  },
  watch: {
    dialog(dl) {
      if (dl) {
        this.selectedGroup = [];
        this.sharingGroups.forEach(g => {
          if (g.group_id == this.collection.group_id)
            this.selectedGroup.push(g);
        });
      }
    },
  },
  methods: {
    ...mapActions('notifications', {
      notifyError: notifyActions.NOTIFY_ERROR,
      notifySuccess: notifyActions.NOTIFY_SUCCESS,
    }),
    dialogClicked() {
      this.dialog = !this.dialog;
    },
    shareOrUnshareCollection() {
      if (this.selectedGroup.length == 0) {
        this.unshareCollection();
        return;
      }
      let url = '/api/collections/' + this.collection.id + '/share';
      let grp_id = this.selectedGroup[0].group_id;
      const request_data = { group_id: grp_id };
      axios
        .post(url, request_data)
        .then(({ data }) => {
          this.$emit('collectionShared', data.collection);
        })
        .catch(err => {
          if (err.response) {
            this.notifyError(
              'Share group request failed with status ' +
                err.status +
                ', error ' +
                err.data.error
            );
          } else {
            this.notifyError('Share group failed, err=' + err);
          }
        });
      this.dialog = false;
    },
    unshareCollection() {
      let url = '/api/collections/' + this.collection.id + '/share';
      axios
        .delete(url)
        .then(({ data }) => {
          this.$emit('collectionShared', data.collection);
        })
        .catch(err => {
          if (err.response) {
            this.notifyError(
              'Unshare group request failed with status ' +
                err.status +
                ', error ' +
                err.data.error
            );
          } else {
            this.notifyError('Unshare group failed, err=' + err);
          }
        });
      this.dialog = false;
    },
    getGroupName(gid) {
      if (gid in this.gid2group) {
        return this.gid2group[gid].name;
      }
      return 'Unknown';
    },
    getTableHeaders() {
      let headers = [
        {
          text: 'Name',
          value: 'name',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Description',
          value: 'description',
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Users',
          value: 'users',
          class: 'text-subtitle-1 font-weight-bold',
        },
      ];
      return headers;
    },
  },
};
</script>

<style lang="scss" scoped></style>
